import { useAlert } from '@ads/components-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  getApiV1ExamRevisionBotexamidLangGridQueryKey,
  usePutApiV1ExamRevisionLangBotexamlangid,
} from '@/shared/api/generated';
import type { UpdateBotExamLangViewModel } from '@/shared/api/generated/types/UpdateBotExamLangViewModel';

/**
 * Edita um idioma de uma prova em revisão via
 * `PUT /api/v1/exam/revision/lang/{botExamLangId}`, invalidando a grid de
 * idiomas ao concluir.
 *
 * @param botExamId - ID da prova bot à qual o idioma pertence (para invalidação)
 * @returns Handler de edição e estado de loading
 *
 * @example
 * ```tsx
 * const { updateLang, isLoading } = useUpdateRevisionLang(botExamId);
 * updateLang(botExamLangId, { cultureInfo: 'en-US', instruction, active: true });
 * ```
 */
export function useUpdateRevisionLang(botExamId: number) {
  const queryClient = useQueryClient();
  const { alert } = useAlert();
  const { t } = useTranslation('examRevision');

  const mutation = usePutApiV1ExamRevisionLangBotexamlangid({
    mutation: {
      onSuccess: () => {
        alert({ alertType: 'success', title: t('langs.toast.editSuccess') });
        void queryClient.invalidateQueries({
          queryKey: getApiV1ExamRevisionBotexamidLangGridQueryKey(botExamId),
        });
      },
      onError: () => alert({ alertType: 'warning', title: t('langs.toast.editError') }),
    },
  });

  const updateLang = (
    botExamLangId: number,
    data: UpdateBotExamLangViewModel,
    callbacks?: { onSuccess?: () => void },
  ) => {
    mutation.mutate(
      { botExamLangId, data },
      callbacks?.onSuccess ? { onSuccess: callbacks.onSuccess } : undefined,
    );
  };

  return { updateLang, isLoading: mutation.isPending } as const;
}
